import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/navigation/Sidebar';
import QuickActions from '../../components/navigation/QuickActions';
import SessionManager from '../../components/navigation/SessionManager';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import HealthHistoryForm from './components/HealthHistoryForm';
import DentalHistoryForm from './components/DentalHistoryForm';
import InsuranceVerification from './components/InsuranceVerification';
import ConsentForms from './components/ConsentForms';
import IntakeProgressBar from './components/IntakeProgressBar';

const DentalPatientIntake = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    personalInfo: {},
    healthHistory: {},
    dentalHistory: {},
    insurance: {},
    consent: {}
  });
  const [autoSaveStatus, setAutoSaveStatus] = useState('saved');

  const steps = [
    { id: 1, name: 'Health History', icon: 'FileText', component: HealthHistoryForm },
    { id: 2, name: 'Dental History', icon: 'Activity', component: DentalHistoryForm },
    { id: 3, name: 'Insurance', icon: 'Shield', component: InsuranceVerification },
    { id: 4, name: 'Consent', icon: 'FileCheck', component: ConsentForms }
  ];

  const handleNext = () => {
    if (currentStep < steps?.length) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleStepClick = (stepId) => {
    setCurrentStep(stepId);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFormDataUpdate = (section, data) => {
    setFormData(prev => ({
      ...prev,
      [section]: { ...prev?.[section], ...data }
    }));
    setAutoSaveStatus('saving');
    setTimeout(() => setAutoSaveStatus('saved'), 1000);
  };

  const handleSubmit = async () => {
    try {
      console.log('Submitting intake form:', formData);
      alert('Patient intake completed successfully!');
      navigate('/patient-dashboard');
    } catch (error) {
      console.error('Intake submission error:', error);
      alert('Failed to submit intake form. Please try again.');
    }
  };

  const CurrentStepComponent = steps?.find(s => s?.id === currentStep)?.component;

  return (
    <div className="flex h-screen bg-background overflow-hidden pt-16">
      <Sidebar isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="sticky top-0 z-30 bg-card border-b border-border px-4 lg:px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl lg:text-2xl font-bold text-foreground">New Patient Intake</h1>
              <p className="text-sm text-muted-foreground mt-1">Complete your health information and consent forms</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden md:flex items-center gap-2 px-3 py-2 bg-background rounded-lg border border-border">
                <Icon 
                  name={autoSaveStatus === 'saving' ? 'Loader' : 'Check'} 
                  size={16} 
                  className={autoSaveStatus === 'saving' ? 'animate-spin text-primary' : 'text-success'}
                />
                <span className="text-sm text-muted-foreground">
                  {autoSaveStatus === 'saving' ? 'Saving...' : 'All changes saved'}
                </span>
              </div>
              <QuickActions />
              <SessionManager 
                onSessionExpired={() => navigate('/login')}
                onSessionExtended={() => console.log('Session extended')}
              />
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Progress Bar */}
            <IntakeProgressBar 
              steps={steps} 
              currentStep={currentStep} 
              onStepClick={handleStepClick}
            />

            {/* Welcome Message */}
            {currentStep === 1 && (
              <div className="bg-primary/5 rounded-lg border border-primary/20 p-4">
                <div className="flex items-start gap-3">
                  <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Welcome to Our Practice!</h3>
                    <p className="text-sm text-muted-foreground mb-2">
                      Please complete this intake form to help us provide you with the best possible care. 
                      Your information is securely stored and protected under HIPAA regulations.
                    </p>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li className="flex items-center gap-2">
                        <Icon name="Clock" size={14} className="text-primary" />
                        <span>Estimated time: 10-15 minutes</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Icon name="Save" size={14} className="text-primary" />
                        <span>Your progress is automatically saved</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Icon name="Lock" size={14} className="text-primary" />
                        <span>All information is encrypted and secure</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Current Step Form */}
            <div className="bg-card rounded-xl border border-border p-6">
              {CurrentStepComponent && (
                <CurrentStepComponent 
                  data={formData}
                  onUpdate={handleFormDataUpdate}
                />
              )}
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between gap-4">
              <Button
                variant="outline"
                size="lg"
                iconName="ChevronLeft"
                iconPosition="left"
                onClick={handlePrevious}
                disabled={currentStep === 1}
              >
                Previous
              </Button>

              <div className="text-sm text-muted-foreground">
                Step {currentStep} of {steps?.length}
              </div>

              {currentStep < steps?.length ? (
                <Button
                  variant="default"
                  size="lg"
                  iconName="ChevronRight"
                  iconPosition="right"
                  onClick={handleNext}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="default"
                  size="lg"
                  iconName="CheckCircle"
                  iconPosition="left"
                  onClick={handleSubmit}
                >
                  Submit Intake
                </Button>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default DentalPatientIntake;